import { memo, useLayoutEffect, useRef, useState } from 'react';
import { Box } from '@mui/material';
import {
  CustomDrawer,
  SIDEBAR_WIDTH,
  SIDEBAR_COLLAPSED_WIDTH,
  TOPO_LOGO_HEIGHT,
  FOOTER_LOGO_HEIGHT,
  sidebarItemStyles,
  sidebarScrollbar,
} from 'theme';
import SideMenu from 'components/SideMenu';
import DrawerTop from './DrawerTop';
import DrawerBottom from './DrawerBottom';

const STORAGE_KEY = 'sidebarCollapsed';
const HIDE_DELAY = 120;

// ---- Barra lateral permanente (sm+) com as 3 etapas ----
function PermanentSidebar() {
  const [collapsed, setCollapsed] = useState(
    () => localStorage.getItem(STORAGE_KEY) === '1',
  );
  const [overlayVisible, setOverlayVisible] = useState(false);
  const hideTimer = useRef(undefined);

  // Reflui o conteúdo da aplicação (AppBar, main, barra de paginação)
  // através da variável CSS --sidebar-width.
  useLayoutEffect(() => {
    document.documentElement.style.setProperty(
      '--sidebar-width',
      `${collapsed ? SIDEBAR_COLLAPSED_WIDTH : SIDEBAR_WIDTH}px`,
    );
    localStorage.setItem(STORAGE_KEY, collapsed ? '1' : '0');
  }, [collapsed]);

  const cancelHide = () => {
    if (hideTimer.current) clearTimeout(hideTimer.current);
  };
  const scheduleHide = () => {
    cancelHide();
    hideTimer.current = setTimeout(() => setOverlayVisible(false), HIDE_DELAY);
  };
  // Apenas a área de MENU ativa o overlay (topo/rodapé são ignorados).
  const openOverlay = () => {
    cancelHide();
    if (collapsed) setOverlayVisible(true);
  };

  const toggle = () => {
    setOverlayVisible(false);
    setCollapsed((c) => !c);
  };

  const width = collapsed ? SIDEBAR_COLLAPSED_WIDTH : SIDEBAR_WIDTH;

  return (
    <Box
      onMouseEnter={cancelHide}
      onMouseLeave={collapsed ? scheduleHide : undefined}
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        height: '100vh',
        width,
        zIndex: 1200,
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: '#FAFAFA',
        borderRight: '1px solid #ececec',
        boxSizing: 'border-box',
      }}
    >
      <DrawerTop collapsed={collapsed} onToggle={toggle} />

      <Box
        onMouseEnter={openOverlay}
        sx={{
          flex: 1,
          overflowY: 'auto',
          overflowX: 'hidden',
          backgroundColor: '#FAFAFA',
          ...sidebarItemStyles,
          ...sidebarScrollbar,
        }}
      >
        <SideMenu collapsed={collapsed} />
      </Box>

      <DrawerBottom collapsed={collapsed} />

      {/* Etapa 3 — overlay com o menu completo sobre o conteúdo */}
      {collapsed && overlayVisible && (
        <Box
          onMouseEnter={cancelHide}
          sx={{
            position: 'absolute',
            left: 0,
            top: TOPO_LOGO_HEIGHT,
            bottom: FOOTER_LOGO_HEIGHT,
            width: SIDEBAR_WIDTH,
            zIndex: 50,
            backgroundColor: '#FFFFFF',
            boxShadow: 6,
            borderRight: '1px solid #EDEDED',
            borderTop: '1px solid #EDEDED',
            borderBottom: '1px solid #EDEDED',
            overflowY: 'auto',
            overflowX: 'hidden',
            ...sidebarItemStyles,
            ...sidebarScrollbar,
            '&::-webkit-scrollbar-track': { backgroundColor: '#FFFFFF' },
          }}
        >
          <SideMenu />
        </Box>
      )}
    </Box>
  );
}

function DrawerMenu(props) {
  const { window } = props;
  const container =
    window !== undefined ? () => window().document.body : undefined;

  return (
    <Box
      component='nav'
      sx={{
        width: { sm: 'var(--sidebar-width, 248px)' },
        flexShrink: { sm: 0 },
      }}
    >
      {/* Mobile — drawer temporário (sem colapso) */}
      <CustomDrawer
        container={container}
        variant='temporary'
        open={props.mobileOpen}
        onClose={props.handleDrawerToggle}
        ModalProps={{ keepMounted: true }} //Better open performance on mobile.
        sx={{ display: { xs: 'block', sm: 'none' } }}
      >
        <div onClick={props.handleDrawerToggle}>
          <DrawerTop />
        </div>
        <Box sx={{ flex: 1, overflowY: 'auto', overflowX: 'hidden' }}>
          <div onClick={props.handleDrawerToggle}>
            <SideMenu />
          </div>
        </Box>
        <DrawerBottom />
      </CustomDrawer>

      {/* Desktop — barra permanente com as 3 etapas */}
      <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
        <PermanentSidebar />
      </Box>
    </Box>
  );
}
export default memo(DrawerMenu);
